import { useEffect, useRef, useState } from 'react';

import { useDragAndDrop } from '@formkit/drag-and-drop/react';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { parse } from 'papaparse';
import { Control, useFieldArray, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';

import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { listKeys } from '@/queries/lists';
import { ApiService } from '@/services/api-v2';

import DeleteIcon from '../icons/delete';
import GripDotVerticalIcon from '../icons/grip-dot-vertical';
import PlusIcon from '../icons/plus';
import UploadIcon from '../icons/upload';
import { Form } from '../ui/form';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../ui/toaster';
import { listFormSchema, ListInputField } from './list-form';

export default function ListDrawer({
  open: openProp,
  onOpenChange,
  noTrigger = false,
  callback,
  defaultValues,
  id,
  readOnly = false,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean, success?: boolean) => void;
  noTrigger?: boolean;
  callback?: (data: z.infer<typeof listFormSchema>) => void;
  defaultValues?: z.infer<typeof listFormSchema>;
  id?: string;
  readOnly?: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(openProp ?? false);

  const form = useForm<z.infer<typeof listFormSchema>>({
    resolver: zodResolver(listFormSchema),
    defaultValues: defaultValues ?? {
      description: '',
      name: '',
      elements: [],
    },
  });
  const { reset } = form;

  useEffect(() => {
    setOpen(openProp ?? false);
    if (!openProp) reset({ name: '', description: '', elements: [] });
  }, [openProp, reset]);

  useEffect(() => {
    if (defaultValues) reset(defaultValues);
  }, [defaultValues, reset]);

  const { mutateAsync: createList } = useMutation({
    mutationFn: (values: z.infer<typeof listFormSchema>) => {
      return ApiService.CreateList({
        name: values.name,
        description: values.description,
        elements: values.elements.map((e) => e.value),
      });
    },
    onSuccess: async () => {
      await queryClient.resetQueries({
        queryKey: listKeys.all,
      });
      handleOpenChange(false, true);
      callback?.(form.getValues());
      toast.success('List added successfully');
    },
    onError: (error) => {
      toast.error(`Error adding list: ${error.message}`);
    },
  });

  const { mutateAsync: updateList } = useMutation({
    mutationFn: (values: z.infer<typeof listFormSchema>) => {
      if (!id) throw new Error('List ID is required');

      return ApiService.UpdateList(id, {
        name: values.name,
        description: values.description,
        elements: values.elements.map((e) => e.value),
      });
    },
    onSuccess: async () => {
      await queryClient.resetQueries({
        queryKey: listKeys.all,
      });
      handleOpenChange(false, true);
      callback?.(form.getValues());
      toast.success('List updated successfully');
    },
    onError: (error) => {
      toast.error(`Error updating list: ${error.message}`);
    },
  });

  const handleOpenChange = (open: boolean, success?: boolean) => {
    onOpenChange?.(open, success);
    setOpen(open);

    if (!open) form.reset();
  };

  const handleSubmit = async (data: z.infer<typeof listFormSchema>) => {
    if (id) {
      await updateList(data);
    } else {
      await createList(data);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        {!noTrigger && (
          <SheetTrigger asChild>
            <Button className="h-full rounded-none p-2" variant="success">
              Create List
            </Button>
          </SheetTrigger>
        )}
        <SheetContent>
          <Form {...form}>
            <form className="flex h-full flex-col" onSubmit={form.handleSubmit(handleSubmit)}>
              <SheetHeader>
                <SheetTitle>{readOnly ? 'View' : id ? 'Update' : 'Create'} List</SheetTitle>
              </SheetHeader>
              <div className="flex flex-col gap-2 overflow-y-auto px-6 pt-4 pb-6">
                <div className="grid grid-cols-1 gap-2">
                  <ListInputField label="Name" name="name" control={form.control} disabled={readOnly} />
                  <ListInputField label="Description" name="description" control={form.control} disabled={readOnly} />
                  <ElementsField control={form.control} readOnly={readOnly} />
                </div>
              </div>

              <SheetFooter>
                <Button variant="grey" type="button" onClick={() => handleOpenChange(false)} disabled={form.formState.isSubmitting}>
                  {readOnly ? 'Close' : 'Cancel'}
                </Button>
                {!readOnly && (
                  <Button variant="success" type="submit" disabled={form.formState.isSubmitting}>
                    Save
                  </Button>
                )}
              </SheetFooter>
            </form>
          </Form>
        </SheetContent>
      </Sheet>
    </>
  );
}

function ElementsField({ control, readOnly = false }: { control: Control<z.infer<typeof listFormSchema>>; readOnly?: boolean }) {
  const { fields, append, remove, swap } = useFieldArray({
    control,
    name: 'elements',
  });

  const [listRef, list, setValues] = useDragAndDrop<HTMLUListElement, (typeof fields)[number]>(fields, {
    dragHandle: '.handle',
    onSort: ({ position, previousPosition }) => {
      swap(position, previousPosition);
    },
  });

  const [newElement, setNewElement] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAdd = () => {
    if (!newElement.trim()) return;
    append({ value: newElement, id: uuidv4() });
    setNewElement('');
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    parse(file, {
      complete: (results) => {
        // Assuming the CSV has no headers and single column format
        const items = results.data
          .flat()
          .filter(Boolean)
          .map((value) => ({
            value: value as string,
            id: uuidv4(),
          }));

        for (const item of items) {
          append(item);
        }
      },
      error: (error) => {
        console.error('Error parsing CSV:', error);
      },
    });

    // Reset the input value so the same file can be selected again
    event.target.value = '';
  };

  useEffect(() => {
    setValues(fields);
  }, [fields, setValues]);

  return (
    <div className="flex flex-col gap-4">
      {!readOnly && (
        <div className="flex items-end gap-2">
          <div className="flex flex-1 flex-col gap-1">
            <Label>Create new item</Label>
            <div className="flex items-center">
              <Input
                value={newElement}
                onChange={(e) => setNewElement(e.target.value)}
                placeholder="Create new item"
                className="rounded-r-none"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAdd();
                  }
                }}
              />
              <Button type="button" aria-label="Add item" onClick={handleAdd} variant="outline" className="h-[41px] rounded-l-none border-2 border-l-0">
                <PlusIcon />
              </Button>
            </div>
          </div>

          <div>
            <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileUpload} className="hidden" />
            <Button className="h-[41px] px-2" icon={<UploadIcon />} type="button" onClick={() => fileInputRef.current?.click()}>
              Upload CSV
            </Button>
          </div>
        </div>
      )}

      <ul ref={listRef} className="flex flex-col gap-2">
        {list.map((field, index) => (
          <li key={field.id}>
            <div className={`bg-grey-1 flex items-center gap-2 rounded p-1 ${readOnly ? '' : 'handle cursor-move'}`}>
              {!readOnly && <GripDotVerticalIcon className="text-secondary-text" />}
              <span className="flex-1 text-sm">{field.value}</span>
              {!readOnly && (
                <Button type="button" variant="ghost" size="sm" aria-label="Remove item" onClick={() => remove(index)} className="text-default-red">
                  <DeleteIcon className="h-4 w-4" />
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
